import { useCallback, useState } from 'react';
import Grid from '@mui/material/Grid2';
import {
  Stack,
  Typography,
  Box,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Button,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import { DAMAGE_CLASS_COLOR_MAP, abyssColors } from '@/theme/colors';
import SharedEsSheetGraph from '../esSheetGraphs/SharedEsSheetGraph';
import DCBarChart from '../esSheetGraphs/DcBarChart';
import DamageClassList from '../esSheetGraphs/DamageClassList';
import {
  RiskAssessmentSummaryCounts,
  StructureForRiskAssessmentQuery,
  SummarySheetGraphsCount,
  useGetPublishedSummarySheetCountsQuery,
  useGetUnpublishedSummarySheetCountsQuery,
  EsSheetStatus,
  StructureForRiskAssessmentDocument,
  useUpdateEsSheetStatusMutation,
} from '@/__generated__/graphql';
import { EsSheetSummaryTable } from './EsSheetSummaryTable';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { ActionButton, ChartAccordian, SummaryCard } from '../shared/styles';
import { ExpandMore } from '@mui/icons-material';
import { useRouter, useFeatureFlag } from '@/hooks';
import { riskView } from '../state/atoms';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useGenerateDataForXlsx } from '../hooks/useGenerateDataForXlsx';
import { useSetAtom } from 'jotai';

const { Published, Unpublished } = EsSheetStatus;
const buttonText = {
  [Published]: 'Unpublish ES Sheet',
  [Unpublished]: 'Publish ES Sheet',
};

type Props = {
  structure: StructureForRiskAssessmentQuery['structure'] | undefined;
  published: boolean;
  riskAssessmentDone: boolean;
  handleClickPlatformSetup: () => void;
};

type CalculatedCountData = {
  key: string;
  calculated: number;
  nonCalculated: number;
};

const mapTotalCalculations = (data: RiskAssessmentSummaryCounts) => {
  return [
    {
      key: 'POF',
      calculated: data?.pofCalculated ?? 0,
      nonCalculated: (data?.totalAssemblies ?? 0) - (data?.pofCalculated ?? 0),
    },
    {
      key: 'COF',
      calculated: data?.cofCalculated ?? 0,
      nonCalculated: (data?.totalAssemblies ?? 0) - (data?.cofCalculated ?? 0),
    },
    {
      key: 'Transition Period',
      calculated: data?.transitionPeriodCalculated ?? 0,
      nonCalculated: (data?.totalAssemblies ?? 0) - (data?.transitionPeriodCalculated ?? 0),
    },
  ];
};

const StructureSetupAlert = ({
  handleClickPlatformSetup,
}: {
  handleClickPlatformSetup: () => void;
}) => {
  return (
    <Alert
      severity="info"
      variant="filled"
      sx={{ alignItems: 'center', ml: '25rem', mr: '25rem' }}
      action={
        <Button
          color="inherit"
          size="small"
          variant="contained"
          onClick={handleClickPlatformSetup}
          sx={{
            ...containedButtonStyle,
            cursor: 'pointer',
            textTransform: 'none',
            backgroundColor: 'white',
            color: abyssColors.primary[500],
            fontWeight: 500,
            fontSize: '12px',
            borderRadius: '4px',
            maxHeight: '32px',
            ':hover': {
              backgroundColor: 'white',
            },
          }}
          startIcon={
            <BuildIcon
              style={{
                width: '1.6rem',
                height: '1.6rem',
                color: abyssColors.primary[500],
              }}
            />
          }
        >
          Asset Setup
        </Button>
      }
    >
      Asset setup is required before performing calculations. Please complete the asset setup.
    </Alert>
  );
};

const PublishedSheetCharts = () => {
  const [governingSHEData, setGoverningSHEData] = useState<SummarySheetGraphsCount[]>([]);
  const [governingFinancialsData, setGoverningFinancialsData] = useState<SummarySheetGraphsCount[]>(
    []
  );
  const [chartData, setChartData] = useState<CalculatedCountData[]>([]);
  const [totalCalculationsExpanded, setTotalCalculationsExpanded] = useState<boolean>(false);
  const structureId = useGetStructureId();

  const { loading } = useGetPublishedSummarySheetCountsQuery({
    variables: {
      SHEInput: {
        structureId,
        key: 'risk_unmitigated_she_category',
      },
      financialsInput: {
        structureId,
        key: 'risk_unmitigated_financials_category',
      },
      structureId,
    },
    onCompleted(data) {
      if (data.governingSHEData) {
        setGoverningSHEData(
          data.governingSHEData
            .filter((item): item is SummarySheetGraphsCount => item !== null && !!item.key)
            .sort((a, b) => {
              if (a.key == undefined) return -1;
              if (b.key == undefined) return 1;
              return a.key > b.key ? -1 : 1;
            })
        );
      }
      if (data.governingFinancialData) {
        setGoverningFinancialsData(
          data.governingFinancialData
            .filter((item): item is SummarySheetGraphsCount => item !== null && !!item.key)
            .sort((a, b) => {
              if (a.key == undefined) return -1;
              if (b.key == undefined) return 1;
              return a.key > b.key ? -1 : 1;
            })
        );
      }
      if (data.calculationCounts) {
        setChartData(mapTotalCalculations(data.calculationCounts));
      }
    },
  });

  return (
    <Box sx={{ mt: 3, mb: 1, mx: '1.6rem' }}>
      <Grid container spacing={2}>
        <Grid size={6}>
          <SummaryCard
            elevation={0}
            sx={{
              height: { md: '216px', lg: '252px' },
            }}
          >
            <Stack>
              <Typography fontSize="1.4rem" fontWeight={500} color={abyssColors.primary[500]}>
                Governing SHE RC values
              </Typography>
              <Stack>
                {!loading && governingSHEData && (
                  <>
                    <DCBarChart damageClassCounts={governingSHEData} />
                    <Stack gap={1} mt={4}>
                      <DamageClassList damageClassCounts={governingSHEData} />
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>
          </SummaryCard>
        </Grid>
        <Grid size={6}>
          <SummaryCard
            elevation={0}
            sx={{
              height: { md: '216px', lg: '252px' },
            }}
          >
            <Stack>
              <Typography fontSize="1.4rem" fontWeight={500} color={abyssColors.primary[500]}>
                Governing Finacial RC values
              </Typography>
              <Stack>
                {!loading && governingFinancialsData && (
                  <>
                    <DCBarChart damageClassCounts={governingFinancialsData} />
                    <Stack gap={1} mt={4}>
                      <DamageClassList damageClassCounts={governingFinancialsData} />
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>
          </SummaryCard>
        </Grid>
      </Grid>
      <ChartAccordian expanded={totalCalculationsExpanded} square elevation={0} disableGutters>
        <AccordionSummary
          sx={{
            padding: 0,
            '.MuiAccordionSummary-content': { mb: 0 },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={0.75}
            onClick={() => {
              setTotalCalculationsExpanded(!totalCalculationsExpanded);
            }}
          >
            <ExpandMore
              sx={{
                fontSize: '2.4rem',
                color: abyssColors.primary[500],
                transform: `rotate(${totalCalculationsExpanded ? '180deg' : '0deg'})`,
              }}
            />
            <Typography fontSize="1.4rem" fontWeight={500} color={abyssColors.primary[500]}>
              Total calculations performed
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={'38px'} paddingTop="20px">
            {chartData.map((graph) => (
              <Grid size={4} key={graph.key}>
                <SummaryCard
                  elevation={0}
                  sx={{
                    height: { md: '200px', lg: '252px' },
                  }}
                >
                  <Stack>
                    <Typography fontSize="1.4rem" fontWeight={500} color={abyssColors.primary[500]}>
                      {graph.key}
                    </Typography>
                    <Box mt="16px">
                      <SharedEsSheetGraph
                        calculations={{
                          calculated: graph.calculated,
                          nonCalculated: graph.nonCalculated,
                        }}
                      />
                    </Box>
                  </Stack>
                </SummaryCard>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </ChartAccordian>
    </Box>
  );
};

const UnpublishedSheetCharts = () => {
  const [damageClassCounts, setDamageClassCounts] = useState<SummarySheetGraphsCount[]>([]);
  const [chartData, setChartData] = useState<CalculatedCountData[]>([]);

  const structureId = useGetStructureId();
  const { loading } = useGetUnpublishedSummarySheetCountsQuery({
    variables: {
      input: { structureId, key: 'risk_damage_class' },
      structureId,
    },
    onCompleted(data) {
      if (data.getSummarySheetGraphsCount) {
        setDamageClassCounts(
          data.getSummarySheetGraphsCount
            .filter(
              (item): item is SummarySheetGraphsCount =>
                item !== null &&
                !!item.key &&
                Object.keys(DAMAGE_CLASS_COLOR_MAP).includes(item.key)
            )
            .sort((a, b) => {
              if (a.key === 'N/A') return 1;
              if (b.key === 'N/A') return -1;
              const numberA = Number.parseInt(a.key?.replace('DC', '') ?? '', 10);
              const numberB = Number.parseInt(b.key?.replace('DC', '') ?? '', 10);
              return numberB - numberA;
            })
        );
      }
      if (data.getRiskAssessmentSummaryCounts) {
        setChartData(mapTotalCalculations(data.getRiskAssessmentSummaryCounts));
      }
    },
  });

  return (
    <Box sx={{ my: 3, mx: '1.6rem' }}>
      <Grid container spacing={1}>
        <Grid size={3}>
          <SummaryCard elevation={0} sx={{ height: { sm: '242px', lg: '280px' } }}>
            <Stack>
              <Typography fontSize="1.4rem" fontWeight={500} color={abyssColors.primary[500]}>
                DC values
              </Typography>
              <Stack>
                {!loading && damageClassCounts && (
                  <>
                    <DCBarChart damageClassCounts={damageClassCounts} />
                    <Stack gap={1} mt={4}>
                      <DamageClassList damageClassCounts={damageClassCounts} />
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>
          </SummaryCard>
        </Grid>
        {chartData.map((graph) => (
          <Grid size={3} key={graph.key}>
            <SummaryCard elevation={0} sx={{ height: { sm: '242px', lg: '280px' } }}>
              <Stack>
                <Typography fontSize="1.4rem" fontWeight={500} color={abyssColors.primary[500]}>
                  {graph.key}
                </Typography>
                <Box mt="16px">
                  <SharedEsSheetGraph
                    calculations={{
                      calculated: graph.calculated,
                      nonCalculated: graph.nonCalculated,
                    }}
                  />
                </Box>
              </Stack>
            </SummaryCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const EsSheetSummary = ({
  structure,
  published,
  riskAssessmentDone,
  handleClickPlatformSetup,
}: Props) => {
  const [updateEsSheetStatus, { loading }] = useUpdateEsSheetStatusMutation();
  const [esSheetStatus, setEsSheetStatus] = useState<EsSheetStatus | null | undefined>(
    structure?.esSheetStatus
  );
  const setView = useSetAtom(riskView);
  const router = useRouter();
  const isMTEngineer = useFeatureFlag('mt-application-engineer');

  const handleEsSheetPublish = async () => {
    updateEsSheetStatus({
      variables: {
        structureId: structure?.id ?? '',
        status: esSheetStatus === Published ? Unpublished : Published,
      },
      refetchQueries: [StructureForRiskAssessmentDocument],
    }).then(({ data }) => {
      const status = data?.updateEsSheetStatus.esSheetStatus;
      setEsSheetStatus(status);
    });
  };

  const navigateToCalculations = useCallback(() => {
    const currentQuery = { ...router.query, view: 'calculations' };
    router.push({
      pathname: router.pathname,
      query: currentQuery,
    });
    setView('calculations');
  }, [router, setView]);

  const { downloadXlsxFile, xlsxLoading } = useGenerateDataForXlsx();

  return (
    <Stack
      height="fit-content"
      padding={2}
      pt={4}
      width="100%"
      maxWidth={{ md: '1332px', sm: '1132px' }}
    >
      {structure && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          marginX="1.6rem"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            paddingTop: '1rem',
            paddingBottom: '2rem',
            backgroundColor: 'white',
          }}
        >
          <Typography variant="h6" fontWeight={500} sx={{ color: abyssColors.primary[500] }}>
            {structure.name} / Summary Sheet
          </Typography>
          <Stack direction="row" gap="1.6rem">
            <ActionButton
              contained={false}
              onClick={() => downloadXlsxFile()}
              disabled={loading}
              sx={{ ...textButtonStyle }}
            >
              <AbyssIcon
                iconName="file-download-line"
                style={{ width: '1.6rem', height: '1.6rem' }}
              />
              <span>{xlsxLoading ? 'Exporting...' : 'Export ES Sheet'}</span>
            </ActionButton>

            {isMTEngineer && (
              <ActionButton
                contained={esSheetStatus === EsSheetStatus.Unpublished}
                onClick={handleEsSheetPublish}
                disabled={loading}
                sx={{
                  ...containedButtonStyle,
                  ':hover': {
                    backgroundColor: abyssColors.primary[500],
                  },
                }}
              >
                <AbyssIcon
                  iconName="file-upload-line"
                  style={{ width: '1.6rem', height: '1.6rem' }}
                />
                <span>{esSheetStatus ? buttonText[esSheetStatus] : 'Publish ES Sheet'}</span>
              </ActionButton>
            )}
            {esSheetStatus === EsSheetStatus.Published && (
              <ActionButton contained onClick={navigateToCalculations}>
                Risk Matrix
              </ActionButton>
            )}
          </Stack>
        </Stack>
      )}
      {!riskAssessmentDone && (
        <StructureSetupAlert handleClickPlatformSetup={handleClickPlatformSetup} />
      )}
      <>
        {published && <PublishedSheetCharts />}
        {!published && <UnpublishedSheetCharts />}
      </>

      {structure && (
        <EsSheetSummaryTable
          structure={structure}
          handleUnpublish={handleEsSheetPublish}
          loadingUnpublish={loading}
          riskAssessmentDone={riskAssessmentDone}
        />
      )}
    </Stack>
  );
};
